/**
 * c-common.js
 * c-プレフィックスセクション・スニペット共通のスクリプト
 */

/* ============================================
   <c-predictive-search> カスタム要素
   sections/c-predictive-search.liquid をフェッチして予測検索結果を表示する
   ============================================ */
if (!window.customElements.get('c-predictive-search')) {
  class CPredictiveSearch extends HTMLElement {
    static activeRequests = 0;

    constructor() {
      super();
      this._listenersAbortController = null;
      this._fetchAbortController = null;
      this._searchForm = null;
      this._queryInput = null;
      this._inputTimer = null;
      this._requestGeneration = 0;
      this._isComposing = false;
      this._loading = false;
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(
        document.createRange().createContextualFragment('<slot name="results"></slot>')
      );
    }

    connectedCallback() {
      this._listenersAbortController = new AbortController();
      this._searchForm = document.querySelector('[aria-owns="' + this.id + '"]');
      this._queryInput = this._searchForm?.elements['q'];
      if (!this._queryInput) return;

      const container = this._searchForm.closest('[data-search-surface]') || this._searchForm.closest('header-search') || this._searchForm.parentElement;
      this._statusElement = container.querySelector('[data-search-status]');
      this._viewAllButton = container.querySelector('[data-search-view-all]');
      this._discoveryElement = container.querySelector('[data-search-discovery]');
      this._retryElement = container.querySelector('[data-search-retry]');
      const options = { signal: this._listenersAbortController.signal };
      this._retryElement?.addEventListener('click', () => this._onInputChanged(), options);
      this._searchForm.addEventListener('submit', (event) => this._onFormSubmitted(event), options);
      this._searchForm.addEventListener('reset', (event) => {
        event.preventDefault();
        this._queryInput.value = '';
        this._onSearchCleared();
        this._queryInput.focus();
      }, options);
      this._queryInput.addEventListener('focus', () => {
        if (this.dataset.engineLoader) import(this.dataset.engineLoader).then(loader => loader.loadSearchEngine()).catch(() => {});
      }, options);
      this._queryInput.addEventListener('input', (event) => this._onInputChanged(event), options);
      this._queryInput.addEventListener('compositionstart', () => {
        this._isComposing = true;
        this._cancelPendingRequest();
        this._clearResults();
        this._setStatus('');
        this._syncControls();
      }, options);
      this._queryInput.addEventListener('compositionend', () => {
        this._isComposing = false;
        this._onInputChanged();
      }, options);
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') this._onSearchCleared(); }, options);
      window.addEventListener('pageshow', (event) => { if (event.persisted) this._onSearchCleared(); }, options);
      this._syncControls();
    }

    disconnectedCallback() {
      if (this._listenersAbortController) this._listenersAbortController.abort();
      this._cancelPendingRequest();
      this._clearResults();
      this._setStatus('');
      this._isComposing = false;
    }

    _cancelPendingRequest() {
      clearTimeout(this._inputTimer);
      this._inputTimer = null;
      this._requestGeneration += 1;
      if (this._fetchAbortController) this._fetchAbortController.abort();
      this._fetchAbortController = null;
      this._setLoading(false);
    }

    _supportsPredictiveApi() {
      try {
        return JSON.parse(document.getElementById('shopify-features')?.textContent || '{}').predictiveSearch === true;
      } catch (error) {
        return false;
      }
    }

    _onInputChanged(event) {
      this._cancelPendingRequest();
      this._clearResults();
      this._setStatus('');
      this._syncControls();
      const query = this._queryInput.value;
      if (!query.trim() || this._isComposing || event?.isComposing) return;
      const generation = this._requestGeneration;
      this._inputTimer = setTimeout(() => {
        this._inputTimer = null;
        this._doPredictiveSearch(query, generation);
      }, 280);
    }

    _onFormSubmitted(event) {
      const query = this._queryInput.value;
      if (!query.trim() || this._isComposing || event.isComposing) {
        event.preventDefault();
        return;
      }
      this._cancelPendingRequest();
    }

    async _doPredictiveSearch(query, generation) {
      if (!this.isConnected || generation !== this._requestGeneration) return;
      const controller = new AbortController();
      this._fetchAbortController = controller;
      this._setLoading(true);
      this._setStatus('loading');
      try {
        const ui = await import(this.dataset.searchUi);
        const section = await ui.renderPredictiveResults({ query, loaderUrl: this.dataset.engineLoader, searchUrl: this._searchForm.action, signal: controller.signal, supportsPredictive: this._supportsPredictiveApi() });
        if (generation !== this._requestGeneration || controller.signal.aborted || !this.isConnected) return;
        this.querySelector('[slot="results"]').replaceChildren(...section.childNodes);
        this._queryInput.setAttribute('aria-expanded', 'true');
        this._setStatus('');
      } catch (error) {
        if (generation === this._requestGeneration && !controller.signal.aborted && this.isConnected) {
          this._clearResults();
          this._setStatus('error');
        }
      } finally {
        if (this._fetchAbortController === controller) {
          this._fetchAbortController = null;
          this._setLoading(false);
        }
      }
    }

    _setLoading(loading) {
      if (loading === this._loading) return;
      this._loading = loading;
      this.setAttribute('aria-busy', String(loading));
      CPredictiveSearch.activeRequests += loading ? 1 : -1;
      if ((loading && CPredictiveSearch.activeRequests === 1) || (!loading && CPredictiveSearch.activeRequests === 0)) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:' + (loading ? 'start' : 'end'), { bubbles: true }));
      }
    }

    _setStatus(state) {
      if (!this._statusElement) return;
      this._statusElement.textContent = this._statusElement.dataset[state] || '';
      if (this._retryElement) this._retryElement.hidden = state !== 'error';
    }

    _syncControls() {
      const hasQuery = this._queryInput.value.trim().length > 0;
      if (this._viewAllButton) this._viewAllButton.disabled = !hasQuery || this._isComposing;
      if (this._discoveryElement && !this._discoveryElement.hasAttribute('data-search-keep-visible')) {
        this._discoveryElement.hidden = hasQuery;
      }
    }

    _clearResults() {
      this.querySelector('[slot="results"]')?.replaceChildren();
      this._queryInput?.setAttribute('aria-expanded', 'false');
    }

    _onSearchCleared() {
      this._cancelPendingRequest();
      this._clearResults();
      this._setStatus('');
      this._syncControls();
    }
  }

  window.customElements.define('c-predictive-search', CPredictiveSearch);
}

(function() {
  'use strict';

  var customScrollbarStates = new WeakMap();

  function clampValue(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getCustomScrollbarMetrics(container, state) {
    var trackWidth = state.track.clientWidth || 0;

    // iOS Safari の scrollWidth バグ回避:
    // flex + overflow-x:auto で scrollWidth が誤った値を返す。
    // getBoundingClientRect は常に正確なので最後の子要素との差分で maxScroll を算出する。
    var maxScroll = 0;
    var totalContentWidth = 0;
    var containerRect = container.getBoundingClientRect();

    if (containerRect.width > 0 && container.lastElementChild) {
      var lastChildRect = container.lastElementChild.getBoundingClientRect();
      // scrollLeft を加算してスクロール位置に関わらず正しい値を得る
      totalContentWidth = lastChildRect.right - containerRect.left + container.scrollLeft;
      maxScroll = Math.max(totalContentWidth - containerRect.width, 0);
    } else {
      // まだレイアウトされていない場合のフォールバック
      totalContentWidth = container.scrollWidth || 0;
      maxScroll = Math.max(container.scrollWidth - container.clientWidth, 0);
    }

    var minThumbWidth = parseFloat(window.getComputedStyle(state.thumb).minWidth) || 24;
    var thumbWidth = trackWidth;

    if (trackWidth > 0 && totalContentWidth > containerRect.width && containerRect.width > 0) {
      thumbWidth = Math.max(
        Math.round((containerRect.width / totalContentWidth) * trackWidth),
        minThumbWidth
      );
      thumbWidth = Math.min(trackWidth, thumbWidth);
    }

    return {
      trackWidth: trackWidth,
      thumbWidth: thumbWidth,
      maxScroll: maxScroll,
      maxThumbOffset: Math.max(trackWidth - thumbWidth, 0)
    };
  }

  function syncCustomScrollbar(container) {
    var state = customScrollbarStates.get(container);
    if (!state) return;

    var metrics = getCustomScrollbarMetrics(container, state);
    var needsScrollbar = metrics.maxScroll > 1 && metrics.trackWidth > 0;

    state.root.classList.toggle('is-scrollable', needsScrollbar);

    // インラインスタイルで直接制御（CSS セレクタ評価を経由しない）
    state.track.style.opacity = needsScrollbar ? '' : '0';
    state.track.style.pointerEvents = needsScrollbar ? '' : 'none';

    if (!needsScrollbar) {
      state.thumb.style.width = '';
      state.thumb.style.transform = 'translate3d(0, 0, 0)';
      return;
    }

    state.thumb.style.width = metrics.thumbWidth + 'px';

    var thumbOffset = metrics.maxScroll > 0
      ? (container.scrollLeft / metrics.maxScroll) * metrics.maxThumbOffset
      : 0;

    state.thumb.style.transform = 'translate3d(' + thumbOffset + 'px, 0, 0)';
  }

  function scrollContainerWithTrackPosition(container, state, clientX) {
    var metrics = getCustomScrollbarMetrics(container, state);
    if (metrics.maxScroll <= 0 || metrics.maxThumbOffset <= 0) return;

    var trackRect = state.track.getBoundingClientRect();
    var targetThumbOffset = clampValue(
      clientX - trackRect.left - metrics.thumbWidth / 2,
      0,
      metrics.maxThumbOffset
    );

    var scrollRatio = targetThumbOffset / metrics.maxThumbOffset;
    container.scrollLeft = scrollRatio * metrics.maxScroll;
  }

  function initCustomScrollbar(container) {
    var state = customScrollbarStates.get(container);
    if (state) {
      syncCustomScrollbar(container);
      return;
    }

    var root = container.closest('[data-c-color-swatches-scroll]');
    if (!root) return;

    var track = root.querySelector('[data-c-color-swatches-scrollbar]');
    var thumb = root.querySelector('[data-c-color-swatches-scrollbar-thumb]');
    if (!track || !thumb) return;

    state = {
      root: root,
      track: track,
      thumb: thumb,
      dragPointerId: null,
      dragStartX: 0,
      dragStartScrollLeft: 0
    };

    customScrollbarStates.set(container, state);

    // 初期状態は非表示（計測後に表示判定）
    track.style.opacity = '0';
    track.style.pointerEvents = 'none';

    track.addEventListener('pointerdown', function(event) {
      if (event.target === thumb) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();
      scrollContainerWithTrackPosition(container, state, event.clientX);
    });

    thumb.addEventListener('pointerdown', function(event) {
      var metrics;

      if (event.pointerType === 'mouse' && event.button !== 0) return;

      metrics = getCustomScrollbarMetrics(container, state);
      if (metrics.maxScroll <= 0 || metrics.maxThumbOffset <= 0) return;

      state.dragPointerId = event.pointerId;
      state.dragStartX = event.clientX;
      state.dragStartScrollLeft = container.scrollLeft;

      state.root.classList.add('is-dragging');
      event.preventDefault();
      event.stopPropagation();

      if (thumb.setPointerCapture) {
        try {
          thumb.setPointerCapture(event.pointerId);
        } catch (error) {}
      }
    });

    thumb.addEventListener('pointermove', function(event) {
      var metrics;
      var deltaX;
      var scrollDelta;

      if (state.dragPointerId !== event.pointerId) return;

      metrics = getCustomScrollbarMetrics(container, state);
      if (metrics.maxScroll <= 0 || metrics.maxThumbOffset <= 0) return;

      deltaX = event.clientX - state.dragStartX;
      scrollDelta = deltaX * (metrics.maxScroll / metrics.maxThumbOffset);
      container.scrollLeft = clampValue(state.dragStartScrollLeft + scrollDelta, 0, metrics.maxScroll);

      event.preventDefault();
      event.stopPropagation();
    });

    function endScrollbarDrag(event) {
      if (state.dragPointerId === null) return;
      if (event && typeof event.pointerId !== 'undefined' && state.dragPointerId !== event.pointerId) return;

      if (thumb.releasePointerCapture && event && typeof event.pointerId !== 'undefined') {
        try {
          thumb.releasePointerCapture(event.pointerId);
        } catch (error) {}
      }

      state.dragPointerId = null;
      state.root.classList.remove('is-dragging');
    }

    thumb.addEventListener('pointerup', endScrollbarDrag);
    thumb.addEventListener('pointercancel', endScrollbarDrag);
    thumb.addEventListener('lostpointercapture', endScrollbarDrag);

    if (typeof ResizeObserver !== 'undefined') {
      state.resizeObserver = new ResizeObserver(function() {
        requestAnimationFrame(function() {
          syncCustomScrollbar(container);
        });
      });
      state.resizeObserver.observe(container);
      state.resizeObserver.observe(root);
    } else if (!state.windowResizeBound) {
      state.windowResizeBound = true;
      window.addEventListener('resize', function() {
        syncCustomScrollbar(container);
      });
    }

    // IntersectionObserver: viewport 進入時に確実に sync する（iOS Safari 対策）
    if (typeof IntersectionObserver !== 'undefined') {
      state.intersectionObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            requestAnimationFrame(function() {
              syncCustomScrollbar(container);
            });
          }
        });
      }, { threshold: 0.01 });
      state.intersectionObserver.observe(root);
    }

    syncCustomScrollbar(container);

    requestAnimationFrame(function() {
      syncCustomScrollbar(container);
      setTimeout(function() { syncCustomScrollbar(container); }, 200);
    });
  }

  /* スウォッチコンテナ内で最も見えている（可視割合が最大かつ 50% 以上の）スウォッチを返す */
  function findBestVisibleSwatch(container, swatchSelector) {
    var cRect = container.getBoundingClientRect();
    var best = null;
    var bestRatio = -1;
    container.querySelectorAll(swatchSelector).forEach(function(s) {
      var r = s.getBoundingClientRect();
      var visible = Math.max(0, Math.min(r.right, cRect.right) - Math.max(r.left, cRect.left));
      var ratio = r.width > 0 ? visible / r.width : 0;
      if (ratio > bestRatio) { bestRatio = ratio; best = s; }
    });
    return bestRatio >= 0.5 ? best : null;
  }

  /* アクティブなスウォッチがコンテナ外に隠れていたら最も見えているものに切り替える */
  function checkAndUpdateActive(container, config) {
    var card = config.getCard(container);
    if (!card) return;
    var active = card.querySelector(config.activeClassSelector);
    if (!active) return;
    var cRect = container.getBoundingClientRect();
    var aRect = active.getBoundingClientRect();
    if (aRect.left >= cRect.left - 1 && aRect.right <= cRect.right + 1) return;
    var best = findBestVisibleSwatch(container, config.swatchSelector);
    if (!best || best === active) return;
    config.setActive(card, best);
  }

  function initSwatchScrollFollow(container, config) {
    initCustomScrollbar(container);

    if (container.hasAttribute('data-c-common-scroll-inited')) {
      syncCustomScrollbar(container);
      return;
    }

    container.setAttribute('data-c-common-scroll-inited', '1');

    container.addEventListener('scroll', function() {
      syncCustomScrollbar(container);
      if (container._scrollFollowTimer) clearTimeout(container._scrollFollowTimer);
      container._scrollFollowTimer = setTimeout(function() {
        container._scrollFollowTimer = null;
        checkAndUpdateActive(container, config);
      }, 200);
    });

    syncCustomScrollbar(container);
  }


  function initAllStaticContainers() {
    var list = [
      { sel: '.c-collection-card__swatches', cfg: 'collection' },
      { sel: '.c-product-card__colors', cfg: 'productCard' },
      { sel: '.c-product-card-block-colors', cfg: 'productCardBlock' },
      { sel: '.c-related-products-swatches', cfg: 'relatedProducts' },
      { sel: '.c-bg3carousel-product-card-colors', cfg: 'bg3ProductCard' },
      { sel: '.c-variations-product-swatches', cfg: 'variations' }
    ];
    var configs = {
      collection: cCollectionCardConfig,
      productCard: cProductCardConfig,
      productCardBlock: cProductCardBlockConfig,
      scrollProductCard: cScrollCarouselProductCardConfig,
      relatedProducts: cRelatedProductsConfig,
      bg3ProductCard: cBg3CarouselProductCardConfig,
      variations: cVariationsConfig
    };
    list.forEach(function(item) {
      var config = configs[item.cfg];
      if (!config) return;
      document.querySelectorAll(item.sel).forEach(function(el) {
        initSwatchScrollFollow(el, config);
      });
    });
  }

  window.addEventListener('load', function() {
    document.querySelectorAll('[data-c-color-swatches-scroll]').forEach(function(root) {
      var container = root.querySelector('[data-swatches-scroll]');
      if (container) syncCustomScrollbar(container);
    });
  });


  /* c-collection-card 用 */
  function cCollectionCardSetActiveSwatch(card, btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (card.classList.contains('cm-search-result-card') && variantId) card.dataset.initialVariantId = variantId;
    /* デフォルト画像を隠す */
    var defaultImg = card.querySelector('[data-default-image]');
    if (defaultImg) {
      defaultImg.style.display = 'none';
      defaultImg.classList.remove('c-collection-card__image--active');
    }
    /* data-variant-id で対応画像を表示 */
    card.querySelectorAll('.c-collection-card__image-container [data-variant-id]').forEach(function(img) {
      var match = img.getAttribute('data-variant-id') === String(variantId);
      img.style.display = match ? '' : 'none';
      img.classList.toggle('c-collection-card__image--active', match);
    });
    /* スウォッチのアクティブクラス更新 */
    card.querySelectorAll('.c-collection-card__swatch').forEach(function(s) {
      s.classList.remove('c-collection-card__swatch--active');
    });
    btn.classList.add('c-collection-card__swatch--active');
    /* アクティブスウォッチをスクロールコンテナの中央に配置（rAF でレイアウト確定後に実行） */
    var swatchContainer = btn.closest('.c-collection-card__swatches');
    if (swatchContainer) {
      requestAnimationFrame(function() {
        var cRect = swatchContainer.getBoundingClientRect();
        var bRect = btn.getBoundingClientRect();
        var delta = (bRect.left + bRect.width / 2) - (cRect.left + cRect.width / 2);
        swatchContainer.scrollLeft += delta;
      });
    }
    /* お気に入りボタンの data-variant-id を選択中バリアントに同期 */
    var favBtn = card.querySelector('.c-collection-card__favorite[data-variant-id]');
    if (favBtn && variantId) favBtn.setAttribute('data-variant-id', variantId);
    /* 画像リンク・タイトルリンクの href をバリアント指定URLに更新 */
    var imageLink = card.querySelector('.c-collection-card__image-link');
    if (imageLink && variantId) {
      if (card.classList.contains('cm-search-result-card')) { var searchImageUrl = new URL(imageLink.href); searchImageUrl.searchParams.set('variant', variantId); imageLink.href = searchImageUrl.href; }
      else imageLink.href = imageLink.href.split('?')[0] + '?variant=' + variantId;
    }
    var titleLink = card.querySelector('.c-collection-card__title a');
    if (titleLink && variantId) {
      if (card.classList.contains('cm-search-result-card')) { var searchTitleUrl = new URL(titleLink.href); searchTitleUrl.searchParams.set('variant', variantId); titleLink.href = searchTitleUrl.href; }
      else titleLink.href = titleLink.href.split('?')[0] + '?variant=' + variantId;
    }
  }

  var cCollectionCardConfig = {
    getCard: function(container) { return container.closest('.c-collection-card'); },
    setActive: cCollectionCardSetActiveSwatch,
    activeClassSelector: '.c-collection-card__swatch--active',
    swatchSelector: '.c-collection-card__swatch',
    margin: 4,
    visibleThreshold: 0.5,
    scrollEndDelay: 100
  };

  /* c-common-variant-preloader:start */
  function cCommonCreateVariantPreloader() {
    var selector = '[data-preload-src], .c-collection-card__image-container img[data-variant-index]';
    var observed = new WeakSet();
    var successful = new Set();
    var jobs = new Map();
    var queue = [];
    var active = 0;
    var observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        observed.delete(entry.target);
        enqueue(entry.target, false);
      });
    }, { rootMargin: '250px 0px', threshold: 0 }) : null;

    function source(el) {
      var explicit = el.getAttribute('data-preload-src');
      var src = explicit || (el.matches('.c-collection-card__image-container img[data-variant-index]') && el.src && !el.complete ? el.src : '');
      if (!src) return '';
      try { return new URL(src, document.baseURI).href; } catch (error) { return ''; }
    }

    function nearViewport(el) {
      if (!el.getBoundingClientRect) return false;
      var rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom >= -250 && rect.top <= window.innerHeight + 250 && rect.right >= 0 && rect.left <= window.innerWidth;
    }

    function observe(el) {
      if (!observer || !el.isConnected || observed.has(el) || !source(el)) return;
      observed.add(el);
      observer.observe(el);
    }

    function pump() {
      while (active < 2 && queue.length) {
        var job = queue.shift();
        var connected = Array.from(job.elements).filter(function(el) {
          if (!el.isConnected) return false;
          if (source(el) === job.src) return true;
          observe(el);
          return false;
        });
        if (!connected.length || (!job.intent && !connected.some(nearViewport))) {
          jobs.delete(job.src);
          connected.forEach(observe);
          continue;
        }
        active += 1;
        start(job);
      }
    }

    function start(job) {
      var image;
      try { image = new Image(); } catch (error) {
        jobs.delete(job.src);
        active -= 1;
        return;
      }
      var finished = false;
      var timer = setTimeout(function() { finish(false, true); }, 30000);
      function finish(ok, cancel) {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        image.onload = image.onerror = null;
        if (cancel) image.removeAttribute('src');
        if (ok) successful.add(job.src);
        jobs.delete(job.src);
        active -= 1;
        pump();
      }
      image.onload = function() { finish(true, false); };
      image.onerror = function() { finish(false, false); };
      if ('fetchPriority' in image) image.fetchPriority = 'low';
      try { image.src = job.src; } catch (error) { finish(false, false); }
    }

    function enqueue(el, intent) {
      if (!el || !el.isConnected) return;
      var src = source(el);
      if (!src || successful.has(src)) return;
      var job = jobs.get(src);
      if (job) {
        job.elements.add(el);
        if (intent && !job.intent) {
          job.intent = true;
          var index = queue.indexOf(job);
          if (index >= 0) { queue.splice(index, 1); queue.unshift(job); }
        }
      } else {
        job = { src: src, elements: new Set([el]), intent: intent };
        jobs.set(src, job);
        if (intent) queue.unshift(job); else queue.push(job);
      }
      pump();
    }

    function eachCandidate(root, callback) {
      if (!root) return;
      if (root.nodeType === 1 && root.matches(selector)) callback(root);
      if (root.querySelectorAll) root.querySelectorAll(selector).forEach(callback);
    }

    return {
      register: function(root) { eachCandidate(root, observe); },
      unregister: function(root) {
        eachCandidate(root, function(el) {
          if (observer && observed.has(el)) observer.unobserve(el);
          observed.delete(el);
        });
      },
      intent: function(el) { enqueue(el, true); },
      selector: selector
    };
  }
  /* c-common-variant-preloader:end */

  /* 共通: アクティブクラス更新 + メイン画像差し替え + お気に入り data-variant-id 同期 */
  function setActiveSwatchWithImage(card, swatch, swatchSelector, activeClass, mainImgSelector, favBtnSelector) {
    card.querySelectorAll(swatchSelector).forEach(function(s) {
      s.classList.remove(activeClass);
    });
    swatch.classList.add(activeClass);
    var variantImg = swatch.querySelector('img');
    var mainImg = card.querySelector(mainImgSelector);
    if (variantImg && mainImg) {
      var largeSrc = variantImg.src.replace(/width=\d+/, 'width=' + (parseInt(mainImg.width, 10) * 2));
      mainImg.src = largeSrc;
    }
    if (favBtnSelector) {
      var variantId = swatch.getAttribute('data-variant-id');
      var favBtn = card.querySelector(favBtnSelector);
      if (favBtn && variantId) favBtn.setAttribute('data-variant-id', variantId);
    }
  }

  /* c-product-card 用 */
  var cProductCardConfig = {
    getCard: function(container) { return container.closest('.c-product-card'); },
    setActive: function(card, swatch) {
      setActiveSwatchWithImage(card, swatch, '.c-product-card__color', 'c-product-card__color--active', '.c-product-card__image img', '.c-product-card__favorite');
    },
    activeClassSelector: '.c-product-card__color--active',
    swatchSelector: '.c-product-card__color',
    margin: 4,
    visibleThreshold: 0.5,
    scrollEndDelay: 100
  };

  /* c-recently-viewed 用 */
  var cRecentlyViewedConfig = {
    getCard: function(container) { return container.closest('.c-recently-viewed-item'); },
    setActive: function(card, swatch) {
      card.querySelectorAll('.c-recently-viewed-swatch').forEach(function(s) {
        s.classList.remove('c-recently-viewed-swatch--active');
      });
      swatch.classList.add('c-recently-viewed-swatch--active');
      var variantImg = swatch.querySelector('img');
      var mainImg = card.querySelector('.c-recently-viewed-image-wrapper img');
      if (variantImg && mainImg) {
        var largeSrc = variantImg.src.replace(/width=\d+/, 'width=' + (mainImg.naturalWidth || 600));
        mainImg.src = largeSrc;
      }
      var variantId = swatch.getAttribute('data-variant-id');
      var favBtn = card.querySelector('.c-recently-viewed-favorite');
      if (favBtn && variantId) favBtn.setAttribute('data-variant-id', variantId);
    },
    activeClassSelector: '.c-recently-viewed-swatch--active',
    swatchSelector: '.c-recently-viewed-swatch',
    margin: 4,
    visibleThreshold: 0.5,
    scrollEndDelay: 100
  };

  /* c-scroll-carousel product 用 */
  var cScrollCarouselProductConfig = {
    getCard: function(container) { return container.closest('.c-scroll-carousel-product-item'); },
    setActive: function(card, swatch) {
      setActiveSwatchWithImage(card, swatch, '.c-scroll-carousel-product-swatch', 'c-scroll-carousel-product-swatch--active', '.c-scroll-carousel-product-image-wrapper > img', '.c-scroll-carousel-product-favorite');
    },
    activeClassSelector: '.c-scroll-carousel-product-swatch--active',
    swatchSelector: '.c-scroll-carousel-product-swatch',
    margin: 4,
    visibleThreshold: 0.5,
    scrollEndDelay: 100
  };

  /* c-scroll-carousel product_card 用 */
  var cScrollCarouselProductCardConfig = {
    getCard: function(container) { return container.closest('.c-scroll-carousel-product-card-link'); },
    setActive: function(card, swatch) {
      setActiveSwatchWithImage(card, swatch, '.c-scroll-carousel-product-card-color', 'c-scroll-carousel-product-card-color--active', '.c-scroll-carousel-product-card-image-wrapper img', '.c-scroll-carousel-product-card-favorite');
    },
    activeClassSelector: '.c-scroll-carousel-product-card-color--active',
    swatchSelector: '.c-scroll-carousel-product-card-color',
    margin: 4,
    visibleThreshold: 0.5,
    scrollEndDelay: 100
  };

  /* c-product-card-block 用 */
  var cProductCardBlockConfig = {
    getCard: function(container) { return container.closest('.c-product-card-block-card'); },
    setActive: function(card, swatch) {
      card.querySelectorAll('.c-product-card-block-color').forEach(function(s) {
        s.classList.remove('c-product-card-block-color--active');
      });
      swatch.classList.add('c-product-card-block-color--active');
      var preloadSrc = swatch.getAttribute('data-preload-src');
      var mainImg = card.querySelector('.c-product-card-block-image--primary');
      if (preloadSrc && mainImg) {
        mainImg.removeAttribute('srcset');
        mainImg.src = preloadSrc;
      }
      var variantId = swatch.getAttribute('data-variant-id');
      var favBtn = card.querySelector('[data-product-favorite][data-variant-id]');
      if (favBtn && variantId) favBtn.setAttribute('data-variant-id', variantId);
      /* 画像リンク・タイトルリンクの href をバリアント指定URLに更新 */
      var imageLink = card.querySelector('.c-product-card-block-media');
      if (imageLink && variantId) {
        imageLink.href = imageLink.href.split('?')[0] + '?variant=' + variantId;
      }
      var titleLink = card.querySelector('.c-product-card-block-title');
      if (titleLink && variantId) {
        titleLink.href = titleLink.href.split('?')[0] + '?variant=' + variantId;
      }
    },
    activeClassSelector: '.c-product-card-block-color--active',
    swatchSelector: '.c-product-card-block-color',
    margin: 4,
    visibleThreshold: 0.5,
    scrollEndDelay: 100
  };

  /* c-bg3carousel product_card 用 */
  var cBg3CarouselProductCardConfig = {
    getCard: function(container) { return container.closest('.c-bg3carousel-product-card-link'); },
    setActive: function(card, swatch) {
      setActiveSwatchWithImage(card, swatch, '.c-bg3carousel-product-card-color', 'c-bg3carousel-product-card-color--active', '.c-bg3carousel-product-card-image-wrapper img', '.c-bg3carousel-product-card-favorite');
    },
    activeClassSelector: '.c-bg3carousel-product-card-color--active',
    swatchSelector: '.c-bg3carousel-product-card-color',
    margin: 4,
    visibleThreshold: 0.5,
    scrollEndDelay: 100
  };

  /* c-related-products 用 */
  var cRelatedProductsConfig = {
    getCard: function(container) { return container.closest('.c-related-products-card'); },
    setActive: function(card, swatch) {
      setActiveSwatchWithImage(card, swatch, '.c-related-products-swatch', 'c-related-products-swatch--active', '.c-related-products-image-wrapper > img', '.c-related-products-favorite');
    },
    activeClassSelector: '.c-related-products-swatch--active',
    swatchSelector: '.c-related-products-swatch',
    margin: 4,
    visibleThreshold: 0.5,
    scrollEndDelay: 100
  };

  /* c-variations 用 */
  var cVariationsConfig = {
    getCard: function(container) { return container.closest('.c-variations-product-item'); },
    setActive: function(card, swatch) {
      setActiveSwatchWithImage(card, swatch, '.c-variations-product-swatch', 'c-variations-product-swatch--active', '.c-variations-product-image-wrapper > img');
    },
    activeClassSelector: '.c-variations-product-swatch--active',
    swatchSelector: '.c-variations-product-swatch',
    margin: 4,
    visibleThreshold: 0.5,
    scrollEndDelay: 100
  };

  /**
   * Swym Wishlist - グローバルお気に入りボタン初期化
   * c-bg3carousel, c-product-card, c-collection-card, c-related-products を対象
   * イベント委譲で動的追加コンテンツにも対応
   */
  function cCommonInitWishlistButtons() {
    if (!window.SwymCallbacks) window.SwymCallbacks = [];
    var globalSelector = '.c-bg3carousel-product-card-favorite[data-product-favorite][data-variant-id][data-product-url], .c-product-card__favorite[data-product-favorite][data-variant-id][data-product-url], .c-collection-card__favorite[data-product-favorite][data-variant-id][data-product-url], .c-related-products-favorite[data-product-favorite][data-variant-id][data-product-url], .c-product-favorite-btn[data-product-id][data-variant-id][data-product-url]';

    function updateFavoriteState(btn, isWishlisted) {
      var emptyIcon = btn.querySelector('.c-favorite-icon--empty, [class*="icon--empty"]');
      var filledIcon = btn.querySelector('.c-favorite-icon--filled, [class*="icon--filled"]');
      if (isWishlisted) {
        btn.classList.add('is-wishlisted');
        btn.setAttribute('aria-label', 'お気に入りから削除');
        if (emptyIcon) emptyIcon.style.display = 'none';
        if (filledIcon) filledIcon.style.display = '';
      } else {
        btn.classList.remove('is-wishlisted');
        btn.setAttribute('aria-label', 'お気に入りに追加');
        if (emptyIcon) emptyIcon.style.display = '';
        if (filledIcon) filledIcon.style.display = 'none';
      }
    }

    function handleFavoriteClick(btn, swat) {
      var empi = parseInt(btn.dataset.productFavorite || btn.dataset.productId, 10);
      var epi = parseInt(btn.dataset.variantId, 10);
      var du = btn.dataset.productUrl;
      if (isNaN(empi) || isNaN(epi) || !du) return;
      /* Swym の du は完全な URL を要求するため、相対パスの場合は絶対 URL に変換 */
      if (du.charAt(0) === '/') du = (window.location.origin || '') + du;

      var isAdded = btn.classList.contains('is-wishlisted');
      var product = { epi: epi, empi: empi, du: du };
      var lid = window.swymSelectedListId;

      if (!lid) {
        swat.createList({ lname: 'My Wishlist' }, function(result) {
          window.swymSelectedListId = result.lid;
          if (isAdded) {
            swat.deleteFromList(result.lid, product, function() { updateFavoriteState(btn, false); }, function(err) { console.error('Swym remove error:', err); });
          } else {
            swat.addToList(result.lid, product, function() { updateFavoriteState(btn, true); }, function(err) { console.error('Swym add error:', err); });
          }
        }, function(err) { console.error('Swym createList error:', err); });
        return;
      }
      if (isAdded) {
        swat.deleteFromList(lid, product, function() { updateFavoriteState(btn, false); }, function(err) { console.error('Swym remove error:', err); });
      } else {
        swat.addToList(lid, product, function() { updateFavoriteState(btn, true); }, function(err) { console.error('Swym add error:', err); });
      }
    }

    function attachFavoriteListeners(buttons, swat) {
      buttons.forEach(function(btn) {
        if (btn.dataset.cCommonFavoriteInited) return;
        btn.dataset.cCommonFavoriteInited = '1';
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          handleFavoriteClick(btn, swat);
        });
      });
    }

    /* イベント委譲: フォールバック・動的追加コンテンツ対応 */
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-product-favorite][data-variant-id][data-product-url], [data-product-id][data-variant-id][data-product-url]');
      if (!btn || !btn.matches('.c-bg3carousel-product-card-favorite, .c-product-card__favorite, .c-collection-card__favorite, .c-related-products-favorite, .c-product-favorite-btn')) return;
      var swat = window.__swymWat;
      if (!swat) return;
      e.preventDefault();
      e.stopPropagation();
      handleFavoriteClick(btn, swat);
    }, true);

    window.SwymCallbacks.push(function(swat) {
      window.__swymWat = swat;

      var buttons = document.querySelectorAll(globalSelector);
      attachFavoriteListeners(buttons, swat);

      if (!buttons.length) return;

      swat.fetchLists({
        callbackFn: function(lists) {
          if (lists && lists.length > 0) {
            if (!window.swymSelectedListId) window.swymSelectedListId = lists[0].lid;
              buttons.forEach(function(btn) {
              var empi = parseInt(btn.dataset.productFavorite || btn.dataset.productId, 10);
              var isWishlisted = false;
              lists.forEach(function(list) {
                if (list.listcontents) {
                  list.listcontents.forEach(function(item) {
                    if (item.empi == empi) isWishlisted = true;
                  });
                }
              });
              updateFavoriteState(btn, isWishlisted);
            });
          } else {
            swat.createList({ lname: 'My Wishlist' }, function(result) {
              window.swymSelectedListId = result.lid;
            }, function(err) { console.error('Swym createList error:', err); });
          }
        },
        errorFn: function(err) { console.error('Swym fetchLists error:', err); }
      });
    });

    /* theme:loading:end でフィルタ等で差し替えられたボタンの状態を更新・リスナーを付与 */
    document.documentElement.addEventListener('theme:loading:end', function() {
      var swat = window.__swymWat;
      if (!swat) return;
      var buttons = document.querySelectorAll(globalSelector);
      attachFavoriteListeners(buttons, swat);
      swat.fetchLists({
        callbackFn: function(lists) {
          if (lists && lists.length > 0 && buttons.length) {
            buttons.forEach(function(btn) {
              var empi = parseInt(btn.dataset.productFavorite || btn.dataset.productId, 10);
              var isWishlisted = false;
              lists.forEach(function(list) {
                if (list.listcontents) {
                  list.listcontents.forEach(function(item) {
                    if (item.empi == empi) isWishlisted = true;
                  });
                }
              });
              updateFavoriteState(btn, isWishlisted);
            });
          }
        },
        errorFn: function() {}
      });
    });

    /* 商品ページ：バリアント変更時に .c-product-favorite-btn の variantId を更新 */
    document.addEventListener('variant:change', function(e) {
      var btn = document.querySelector('.c-product-favorite-btn');
      var variant = e.detail && e.detail.variant;
      if (btn && variant) btn.dataset.variantId = variant.id;
    });
  }

  /* ============================================
     c-product-main: buy-buttons 再レンダリングフォールバック
     theme.js の onRerender_fn が <buy-buttons> を消した場合に復元する

     動作原理:
     - product:rerender は form 要素上で bubbles:false で発火する（document には届かない）
     - product-rerender カスタム要素は connectedCallback 時（HTML パース直後）にリスナーを登録する
     - c-common.js の DOMContentLoaded ハンドラはそれより後に実行されるため、
       このリスナーは必ず product-rerender のハンドラ完了後に実行される
     - 先行した product-rerender ハンドラが <buy-buttons> を消した後、
       HTML フラグメントから復元する
     ============================================ */
  function cProductMainPatchBuyButtonsRerender() {
    var section = document.querySelector('.shopify-section--c-product-main');
    if (!section) return;

    var productRerender = section.querySelector('product-rerender[observe-form]');
    if (!productRerender) return;

    var formId = productRerender.getAttribute('observe-form');
    var form = document.forms[formId];
    if (!form) return;

    form.addEventListener('product:rerender', function(event) {
      var wrapper = section.querySelector('[data-block-type="buy-buttons"]');
      if (!wrapper) return;

      // buy-buttons が存在すれば問題なし
      if (wrapper.querySelector('buy-buttons')) return;

      // buy-buttons が消えている → HTML フラグメントから復元
      var fragment = event.detail && event.detail.htmlFragment;
      if (!fragment) return;

      var blockId = wrapper.getAttribute('data-block-id');
      var matchingBlock = fragment.querySelector(
        '[data-block-type="buy-buttons"][data-block-id="' + blockId + '"]'
      );

      if (matchingBlock) {
        wrapper.replaceWith(matchingBlock);
      }
    });
  }

  /* Swym ウィッシュリストコンテナを画像ラッパー内に移動（バリアント切り替え後のズレ防止） */
  function cCollectionCardInitSwymPosition() {
    var cardConfigs = [
      { cardSel: 'product-card.c-collection-card',       figureSel: '.c-collection-card__image-wrapper' },
      { cardSel: 'product-card.c-product-card-block-card', figureSel: '.c-product-card-block-figure' }
    ];

    function moveSwymContainers() {
      cardConfigs.forEach(function(cfg) {
        document.querySelectorAll(cfg.cardSel + ' > .swym-wishlist-collections-v2-container').forEach(function(container) {
          var card = container.closest(cfg.cardSel);
          if (!card) return;
          var figure = card.querySelector(cfg.figureSel);
          if (figure && container.parentNode !== figure) {
            figure.appendChild(container);
          }
        });
      });
    }

    moveSwymContainers();

    var observer = new MutationObserver(function(mutations) {
      var shouldMove = false;
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1 && node.classList && node.classList.contains('swym-wishlist-collections-v2-container')) {
            shouldMove = true;
          }
        });
      });
      if (shouldMove) setTimeout(moveSwymContainers, 0);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ページ読み込み時に Liquid がアクティブにしたスウォッチのバリアント ID をリンクに反映 */
  function cCollectionCardInitVariantLinks() {
    document.querySelectorAll('.c-collection-card').forEach(function(card) {
      var activeSwatch = card.querySelector('.c-collection-card__swatch--active');
      if (!activeSwatch) return;
      var variantId = card.classList.contains('cm-search-result-card') ? card.dataset.initialVariantId || activeSwatch.getAttribute('data-variant-id') : activeSwatch.getAttribute('data-variant-id');
      if (!variantId) return;
      var imageLink = card.querySelector('.c-collection-card__image-link');
      if (imageLink) {
        if (card.classList.contains('cm-search-result-card')) { var searchImageUrl = new URL(imageLink.href); searchImageUrl.searchParams.set('variant', variantId); imageLink.href = searchImageUrl.href; }
        else imageLink.href = imageLink.href.split('?')[0] + '?variant=' + variantId;
      }
      var titleLink = card.querySelector('.c-collection-card__title a');
      if (titleLink) {
        if (card.classList.contains('cm-search-result-card')) { var searchTitleUrl = new URL(titleLink.href); searchTitleUrl.searchParams.set('variant', variantId); titleLink.href = searchTitleUrl.href; }
        else titleLink.href = titleLink.href.split('?')[0] + '?variant=' + variantId;
      }
    });
  }

  function cProductCardBlockInitVariantLinks() {
    document.querySelectorAll('.c-product-card-block-card').forEach(function(card) {
      var activeSwatch = card.querySelector('.c-product-card-block-color--active');
      if (!activeSwatch) return;
      var variantId = activeSwatch.getAttribute('data-variant-id');
      if (!variantId) return;
      var imageLink = card.querySelector('.c-product-card-block-media');
      if (imageLink) imageLink.href = imageLink.href.split('?')[0] + '?variant=' + variantId;
      var titleLink = card.querySelector('.c-product-card-block-title');
      if (titleLink) titleLink.href = titleLink.href.split('?')[0] + '?variant=' + variantId;
    });
  }

  /* 初期化 */
  function init() {
    cCommonInitWishlistButtons();
    cCollectionCardInitSwymPosition();
    cCollectionCardInitVariantLinks();
    cProductCardBlockInitVariantLinks();

    /* c-collection-card: スウォッチクリックでアクティブ切り替え（capture で先行して処理） */
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.c-collection-card .c-collection-card__swatch');
      if (!btn) return;
      var card = btn.closest('.c-collection-card');
      if (!card) return;
      cCollectionCardSetActiveSwatch(card, btn);
    }, true);

    /* c-collection-card: ナビボタン（前後矢印）クリックで画像切り替え */
    document.addEventListener('click', function(e) {
      var navBtn = e.target.closest('.c-collection-card__nav');
      if (!navBtn) return;
      var card = navBtn.closest('.c-collection-card');
      if (!card) return;
      e.preventDefault();
      e.stopPropagation();
      var direction = navBtn.getAttribute('data-nav-direction');
      var images = Array.from(card.querySelectorAll('.c-collection-card__image-container [data-variant-index]'));
      if (images.length <= 1) return;
      var activeImg = images.find(function(img) { return img.classList.contains('c-collection-card__image--active'); });
      if (!activeImg) return;
      var currentIdx = parseInt(activeImg.getAttribute('data-variant-index'), 10);
      var nextIdx = direction === 'next' ? currentIdx + 1 : currentIdx - 1;
      nextIdx = Math.max(0, Math.min(images.length - 1, nextIdx));
      if (nextIdx === currentIdx) return;
      var nextImg = images.find(function(img) {
        return parseInt(img.getAttribute('data-variant-index'), 10) === nextIdx;
      });
      var nextVariantId = nextImg ? nextImg.getAttribute('data-variant-id') : null;
      var nextSwatch = nextVariantId
        ? card.querySelector('.c-collection-card__swatch[data-variant-id="' + nextVariantId + '"]')
        : null;
      if (nextSwatch) {
        cCollectionCardSetActiveSwatch(card, nextSwatch);
      } else {
        images.forEach(function(img) {
          var isNext = parseInt(img.getAttribute('data-variant-index'), 10) === nextIdx;
          img.style.display = isNext ? '' : 'none';
          img.classList.toggle('c-collection-card__image--active', isNext);
        });
      }
    });

    /* c-collection-card: モバイルフリックで画像スワイプ + スウォッチ連動 */
    (function() {
      var cCardTouchData = null;

      document.addEventListener('touchstart', function(e) {
        var link = e.target.closest('.c-collection-card__image-link');
        if (!link) { cCardTouchData = null; return; }
        var card = link.closest('.c-collection-card');
        cCardTouchData = {
          link: link,
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          cardLeft: card ? card.getBoundingClientRect().left : null
        };
      }, { passive: true });

      document.addEventListener('touchend', function(e) {
        if (!cCardTouchData) return;
        var dx = e.changedTouches[0].clientX - cCardTouchData.x;
        var dy = e.changedTouches[0].clientY - cCardTouchData.y;
        var link = cCardTouchData.link;
        var cardLeft = cCardTouchData.cardLeft;
        cCardTouchData = null;
        if (Math.abs(dx) < 30 || Math.abs(dx) <= Math.abs(dy)) return;

        var card = link.closest('.c-collection-card');

        /* カードが移動していれば外側カルーセルのスクロールと判断してキャンセル */
        if (card && cardLeft !== null && Math.abs(card.getBoundingClientRect().left - cardLeft) > 20) return;
        if (!card) return;
        var images = Array.from(card.querySelectorAll('.c-collection-card__image-container [data-variant-index]'));
        if (images.length <= 1) return;

        var activeImg = images.find(function(img) { return img.classList.contains('c-collection-card__image--active'); });
        if (!activeImg) return;
        var currentIdx = parseInt(activeImg.getAttribute('data-variant-index'), 10);
        var nextIdx = dx < 0 ? currentIdx + 1 : currentIdx - 1;
        nextIdx = Math.max(0, Math.min(images.length - 1, nextIdx));
        if (nextIdx === currentIdx) return;

        link._swipePrevented = true;
        var nextImg = images.find(function(img) {
          return parseInt(img.getAttribute('data-variant-index'), 10) === nextIdx;
        });
        var nextVariantId = nextImg ? nextImg.getAttribute('data-variant-id') : null;
        var nextSwatch = nextVariantId
          ? card.querySelector('.c-collection-card__swatch[data-variant-id="' + nextVariantId + '"]')
          : null;
        if (nextSwatch) {
          cCollectionCardSetActiveSwatch(card, nextSwatch);
        } else {
          images.forEach(function(img) {
            var isNext = parseInt(img.getAttribute('data-variant-index'), 10) === nextIdx;
            img.style.display = isNext ? '' : 'none';
            img.classList.toggle('c-collection-card__image--active', isNext);
          });
        }
      }, { passive: true });

      document.addEventListener('click', function(e) {
        var link = e.target.closest('.c-collection-card__image-link');
        if (link && link._swipePrevented) {
          e.preventDefault();
          link._swipePrevented = false;
        }
      }, true);
    })();

    /* 画面付近または操作対象のバリアント画像のみ先読み */
    var variantPreloader = cCommonCreateVariantPreloader();
    function registerVariantPreloads() { variantPreloader.register(document); }
    function preloadOnIntent(event) {
      var target = event.target && event.target.closest && event.target.closest(variantPreloader.selector);
      if (target) variantPreloader.intent(target);
    }
    document.addEventListener('pointerover', preloadOnIntent, { passive: true });
    document.addEventListener('pointerdown', preloadOnIntent, { passive: true });
    document.addEventListener('focusin', preloadOnIntent);
    document.addEventListener('touchstart', preloadOnIntent, { passive: true });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        (typeof requestIdleCallback !== 'undefined') ? requestIdleCallback(registerVariantPreloads, { timeout: 3000 }) : setTimeout(registerVariantPreloads, 100);
      });
    } else {
      (typeof requestIdleCallback !== 'undefined') ? requestIdleCallback(registerVariantPreloads, { timeout: 3000 }) : setTimeout(registerVariantPreloads, 100);
    }
    /* 動的追加コンテンツ（最近見た商品など）も同じ判定を使う */
    document.documentElement.addEventListener('theme:loading:end', function() {
      setTimeout(registerVariantPreloads, 200);
    });
    if (typeof MutationObserver !== 'undefined') {
      var preloadObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mut) {
          if (mut.removedNodes) mut.removedNodes.forEach(variantPreloader.unregister);
          if (mut.addedNodes) mut.addedNodes.forEach(variantPreloader.register);
        });
      });
      preloadObserver.observe(document.body, { childList: true, subtree: true });
    }

    /* c-main-collection: --active クラス付きスウォッチを全て中央スクロール（Liquid pre-activate 対応） */
    function cCollectionInitScrollActiveSwatches() {
      document.querySelectorAll('.c-collection-card__swatches').forEach(function(container) {
        var active = container.querySelector('.c-collection-card__swatch--active');
        if (!active) return;
        var cRect = container.getBoundingClientRect();
        var bRect = active.getBoundingClientRect();
        var delta = (bRect.left + bRect.width / 2) - (cRect.left + cRect.width / 2);
        container.scrollLeft += delta;
      });
    }

    /* c-main-collection: カラーフィルター選択時に商品カードのスウォッチを自動アクティブ化 */
    function cCollectionAutoActivateColorFilter(container) {
      var activeColor = container.dataset.activeColor;
      if (!activeColor) return;
      container.querySelectorAll('.c-collection-card').forEach(function(card) {
        /* 完全一致を優先し、なければ部分一致（"ブラック" → "ワイルドブラック"/"ブラックアンバー" など）で選択 */
        var matchSwatch = card.querySelector('.c-collection-card__swatch[data-color-value="' + activeColor + '"]');
        if (!matchSwatch) {
          var swatches = card.querySelectorAll('.c-collection-card__swatch[data-color-value]');
          for (var i = 0; i < swatches.length; i++) {
            var cv = swatches[i].dataset.colorValue || '';
            if (cv !== activeColor && cv.indexOf(activeColor) !== -1) {
              matchSwatch = swatches[i];
              break;
            }
          }
        }
        if (matchSwatch) {
          cCollectionCardSetActiveSwatch(card, matchSwatch);
        }
      });
    }

    /* メインコレクション: 色フィルターなし（data-active-color なし）のとき Liquid が付けたスウォッチ active を外す */
    function cMainCollectionStripSwatchActiveWhenNoColorFilter() {
      document.querySelectorAll('.c-main-collection__product-list:not([data-active-color])').forEach(function(list) {
        if (list.classList.contains('cm-search-product-results')) return;
        list.querySelectorAll('.c-collection-card__swatch--active').forEach(function(btn) {
          btn.classList.remove('c-collection-card__swatch--active');
        });
      });
    }

    document.querySelectorAll('[data-active-color]').forEach(function(container) {
      cCollectionAutoActivateColorFilter(container);
    });
    cMainCollectionStripSwatchActiveWhenNoColorFilter();
    /* Liquid pre-activate されたスウォッチを中央スクロール（rAF でレイアウト確定後に実行） */
    requestAnimationFrame(cCollectionInitScrollActiveSwatches);
    /* theme:loading:end は replaceChildren より前に発火する。1フレームでは差し替え直後の DOM とずれることがあるため二重 rAF で同期 */
    document.documentElement.addEventListener('theme:loading:end', function() {
      requestAnimationFrame(function() {
        requestAnimationFrame(function() {
          document.querySelectorAll('[data-active-color]').forEach(function(container) {
            cCollectionAutoActivateColorFilter(container);
          });
          cMainCollectionStripSwatchActiveWhenNoColorFilter();
          cCollectionInitScrollActiveSwatches();
        });
      });
    });

    /* c-product-card: クリックでアクティブ切り替え + 画像差し替え */
    document.addEventListener('click', function(e) {
      var swatch = e.target.closest('.c-product-card [data-product-color-swatch]');
      if (!swatch) return;
      var card = swatch.closest('.c-product-card');
      if (!card) return;
      e.preventDefault();
      e.stopPropagation();
      cProductCardConfig.setActive(card, swatch);
    }, true);

    /* c-scroll-carousel product_card: クリックでアクティブ切り替え + 画像差し替え */
    document.addEventListener('click', function(e) {
      var swatch = e.target.closest('.c-scroll-carousel-product-card-color');
      if (!swatch) return;
      var card = swatch.closest('.c-scroll-carousel-product-card-link');
      if (!card) return;
      e.preventDefault();
      e.stopPropagation();
      cScrollCarouselProductCardConfig.setActive(card, swatch);
    }, true);

    /* c-bg3carousel product_card: クリックでアクティブ切り替え + 画像差し替え + お気に入り同期 */
    document.addEventListener('click', function(e) {
      var swatch = e.target.closest('.c-bg3carousel-product-card-color');
      if (!swatch) return;
      var card = swatch.closest('.c-bg3carousel-product-card-link');
      if (!card) return;
      e.preventDefault();
      e.stopPropagation();
      cBg3CarouselProductCardConfig.setActive(card, swatch);
    }, true);

    /* 静的コンテナ: スクロール追従（facet更新でDOM差し替え後も再初期化） */
    initAllStaticContainers();

    /**
     * c-product-gallery（矢印）: ScrollCarousel は scroll で prev/next の disabled を付けるが、
     * 初回は scroll が走らず先頭で < が残ることがある → レイアウト確定後に scroll を1回送る。
     */
    function cProductGalleryKickCarouselNavScroll(root) {
      var scope = root && root.nodeType === 1 ? root : document;
      scope.querySelectorAll('[data-c-product-gallery] .product-gallery__carousel-with-arrows scroll-carousel.product-gallery__carousel').forEach(function(carousel) {
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            var wrap = carousel.parentElement;
            var prevBtn = wrap && wrap.querySelector('carousel-prev-button button');
            var nextBtn = wrap && wrap.querySelector('carousel-next-button button');
            if (!prevBtn || !nextBtn) return;
            if (carousel.scrollWidth <= carousel.clientWidth + 1) {
              prevBtn.disabled = true;
              nextBtn.disabled = true;
              return;
            }
            carousel.dispatchEvent(new Event('scroll', { bubbles: false }));
          });
        });
      });
    }

    /* facet:update 後・セクション再読込後に再初期化 */
    document.documentElement.addEventListener('theme:loading:end', function() {
      setTimeout(initAllStaticContainers, 0);
      setTimeout(cCollectionCardInitVariantLinks, 0);
      setTimeout(cProductCardBlockInitVariantLinks, 0);
      setTimeout(cProductGalleryKickCarouselNavScroll, 0);
    });
    document.addEventListener('shopify:section:load', function(ev) {
      setTimeout(initAllStaticContainers, 0);
      setTimeout(cCollectionCardInitVariantLinks, 0);
      setTimeout(cProductCardBlockInitVariantLinks, 0);
      setTimeout(function() {
        cProductGalleryKickCarouselNavScroll(ev.target);
      }, 0);
    });

    cProductGalleryKickCarouselNavScroll();
    cProductMainPatchBuyButtonsRerender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* 動的追加された要素用 */
  window.CCommon = window.CCommon || {};
  window.CCommon.initSwatchScrollFollow = initSwatchScrollFollow;
  window.CCommon.initAllStaticContainers = initAllStaticContainers;
  window.CCommon.cCollectionCardConfig = cCollectionCardConfig;
  window.CCommon.cRecentlyViewedConfig = cRecentlyViewedConfig;
  window.CCommon.cScrollCarouselProductConfig = cScrollCarouselProductConfig;
})();

/**
 * cAccordionInit - 共通アコーディオン初期化関数
 * c-scroll-carousel / c-variations / c-related-products / c-rich-text で共有
 *
 * @param {Object} config
 * @param {string} config.toggleId               - toggle button の HTML id
 * @param {string} config.contentId              - content div の HTML id
 * @param {string} config.sectionElementId       - section 要素の HTML id
 * @param {boolean} [config.carouselOverflow]    - カルーセルはみ出し用 overflow 制御 (default: false)
 * @param {number}  [config.carouselOverflowBreakpoint] - overflow 制御のモバイル上限幅 (default: 990)
 * @param {boolean} [config.toggleNullable]      - toggle が null でも続行 (default: false)
 */
function cAccordionInit(config) {
  var toggleId                  = config.toggleId;
  var contentId                 = config.contentId;
  var sectionElementId          = config.sectionElementId;
  var carouselOverflow          = config.carouselOverflow || false;
  var carouselOverflowBreakpoint = config.carouselOverflowBreakpoint || 990;
  var toggleNullable            = config.toggleNullable || false;

  var toggle    = document.getElementById(toggleId);
  var content   = document.getElementById(contentId);
  var sectionEl = document.getElementById(sectionElementId);

  if (!content || !sectionEl) return;
  if (!toggleNullable && !toggle) return;

  function isMobileOverflow() {
    return window.innerWidth < carouselOverflowBreakpoint;
  }

  function isAccordionEnabled() {
    var isDesktop = window.innerWidth >= 990;
    return isDesktop
      ? sectionEl.dataset.accordionDesktop === 'true'
      : sectionEl.dataset.accordionMobile === 'true';
  }

  function setInitialState() {
    var isDesktop   = window.innerWidth >= 990;
    var defaultOpen = isDesktop
      ? sectionEl.dataset.defaultOpenDesktop === 'true'
      : sectionEl.dataset.defaultOpenMobile === 'true';

    if (!isAccordionEnabled()) {
      content.classList.remove('collapsed');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (carouselOverflow && isMobileOverflow()) content.style.overflow = 'visible';
      return;
    }

    if (defaultOpen) {
      content.classList.remove('collapsed');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (carouselOverflow && isMobileOverflow()) content.style.overflow = 'visible';
    } else {
      content.classList.add('collapsed');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (carouselOverflow && isMobileOverflow()) content.style.overflow = '';
    }
  }

  setInitialState();

  if (!toggle) return;

  toggle.addEventListener('click', function() {
    if (!isAccordionEnabled()) return;

    var isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      if (carouselOverflow && isMobileOverflow()) content.style.overflow = 'hidden';
      content.classList.add('collapsed');
      toggle.setAttribute('aria-expanded', 'false');
    } else {
      content.classList.remove('collapsed');
      toggle.setAttribute('aria-expanded', 'true');
      if (carouselOverflow && isMobileOverflow()) {
        var onTransitionEnd = function() {
          content.style.overflow = 'visible';
          content.removeEventListener('transitionend', onTransitionEnd);
        };
        content.addEventListener('transitionend', onTransitionEnd);
      }
    }
  });
}

/* facets-drawer をヘッダーより前面に表示する
   FacetsDrawer は shouldAppendToBody=false のため祖先のスタッキングコンテキストに閉じ込められることがある。
   shouldAppendToBody=true にして開いたときに body 直下に移動させることで確実にヘッダーより上に表示する。 */
customElements.whenDefined('facets-drawer').then(function() {
  var FacetsDrawerClass = window.customElements.get('facets-drawer');
  if (FacetsDrawerClass && FacetsDrawerClass.prototype) {
    Object.defineProperty(FacetsDrawerClass.prototype, 'shouldAppendToBody', {
      get: function() { return true; },
      configurable: true
    });
  }
});

/* ページネーションクリック時にセクション先頭へスクロール */
document.addEventListener('click', function(e) {
  var paginationLink = e.target.closest('.pagination__link');
  if (!paginationLink) return;
  var paginationSection = paginationLink.closest('[id^="shopify-section-"]');
  if (!paginationSection) return;
  var stickyHeight = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height')
  ) || 0;
  var sectionTop = paginationSection.getBoundingClientRect().top + (window.cmProductPageScroll?.getY() ?? window.pageYOffset) - stickyHeight;
  (window.cmProductPageScroll || window).scrollTo({ top: sectionTop, behavior: 'smooth' });
});
